// Bulk-import leads from CSV. Three steps in one modal:
//   1) Source — paste CSV text or upload a .csv file
//   2) Map   — match each CSV column to a Lead field (auto-guesses by header)
//   3) Review — preview parsed rows, see what'll fail (no title), submit
//
// We keep the CSV parser inline (no PapaParse dep) — small RFC-4180-tolerant
// reader that handles quoted fields, escaped quotes, and embedded newlines.
// Server caps at 500 rows/call and reports per-row errors back; on submit
// the modal shows a summary banner and stays open so the user can fix bad
// rows (we filter them out of the textarea automatically for re-submit).

import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Upload, FileText, ArrowRight, ArrowLeft, AlertCircle, AlertTriangle, CheckCircle2, Download, X } from "lucide-react";
import { Select as AntSelect } from "antd";
import Modal from "../overlays/Modal";
import { Button, Badge } from "../atoms";
import { useBulkCreateLeads, useBulkDuplicateCheck } from "../hooks/useLeads";
import { useSelectedProject } from "../contexts/SelectedProjectProvider";
import { normalizeLeadStatus } from "../lib/normalizeLeadStatus";

// Lead fields the user can map a CSV column to. Order matters for the dropdown.
const FIELDS = [
  { key: "title", label: "Title (deal name / POC Name)", required: true },
  { key: "brandName", label: "Brand / company" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "website", label: "Website" },
  { key: "value", label: "Value" },
  { key: "currency", label: "Currency (USD, INR, …)" },
  { key: "source", label: "Source" },
  { key: "sourceChannel", label: "Source channel" },
  { key: "status", label: "Status (open, hot, warm, cold, won, lost)" },
  { key: "labels", label: "Labels (comma-separated)" },
  { key: "comments", label: "Comments / notes" },
  { key: "nextActivity", label: "Next activity" },
];
const FIELD_KEYS = new Set(FIELDS.map((f) => f.key));

// Header guesser — maps common column names to lead field keys.
const HEADER_GUESS = {
  title: "title", name: "title", deal: "title", "deal name": "title",
  "poc name": "title", poc: "title", poc_name: "title", "point of contact": "title",
  brand: "brandName", brandname: "brandName", company: "brandName", "company name": "brandName", organization: "brandName",
  email: "email", "email address": "email", "e-mail": "email",
  phone: "phone", mobile: "phone", number: "phone", "phone number": "phone",
  website: "website", url: "website", site: "website", web: "website",
  value: "value", amount: "value", deal_value: "value", "deal value": "value", price: "value",
  currency: "currency", ccy: "currency",
  source: "source", "lead source": "source",
  channel: "sourceChannel", "source channel": "sourceChannel",
  status: "status", state: "status", stage: "status",
  labels: "labels", tags: "labels", label: "labels", tag: "labels",
  comments: "comments", notes: "comments", note: "comments", description: "comments",
  "next activity": "nextActivity", next_activity: "nextActivity", "next step": "nextActivity",
};

function guessField(header) {
  const norm = String(header || "").trim().toLowerCase();
  if (!norm) return "";
  if (HEADER_GUESS[norm]) return HEADER_GUESS[norm];
  // Loose match: substring against guess keys.
  for (const k of Object.keys(HEADER_GUESS)) {
    if (norm.includes(k)) return HEADER_GUESS[k];
  }
  return "";
}

// Wrap a single CSV cell — quote it when it contains a comma, quote, or
// newline, and escape embedded quotes by doubling them. Same rules the
// inline parseCsv() above accepts on the read side.
function csvCell(v) {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// Build a starter CSV that exercises every field the importer knows about.
// One header row + two illustrative example rows. The headers use the exact
// field keys (title, brandName, …) so the auto-mapper hits 100% match on
// step 2 and the user can clear the examples and paste their own data.
function buildTemplateCsv() {
  const headers = FIELDS.map((f) => f.key);
  const examples = [
    {
      title: "Acme renewal — Q3",
      brandName: "Acme Inc",
      email: "jane@acme.com",
      phone: "+15551234567",
      website: "https://acme.com",
      value: "12000",
      currency: "USD",
      source: "Referral",
      sourceChannel: "Partner",
      status: "open",
      labels: "renewal,enterprise",
      comments: "Talked to Jane on call, wants pricing by EoQ.",
      nextActivity: "Send proposal",
    },
    {
      title: "Beta Corp — new logo",
      brandName: "Beta Corp",
      email: "ops@beta.co",
      phone: "+919876543210",
      website: "beta.co",
      value: "45000",
      currency: "INR",
      source: "Website",
      sourceChannel: "Inbound",
      status: "hot",
      labels: "smb",
      comments: "",
      nextActivity: "Schedule discovery call",
    },
  ];
  const lines = [headers.join(",")];
  for (const ex of examples) {
    lines.push(headers.map((h) => csvCell(ex[h] || "")).join(","));
  }
  return lines.join("\n") + "\n";
}

function downloadTemplate() {
  const csv = buildTemplateCsv();
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "leads-import-template.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser a beat to start the download before we revoke.
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

// RFC-4180-ish CSV parser. Returns { headers, rows } where rows is an array
// of objects keyed by header. Handles "quoted, fields" with "escaped""quotes"
// and \n inside quotes. Empty trailing rows are dropped.
function parseCsv(text) {
  const cells = [[]];
  let cur = "";
  let inQuotes = false;
  let row = 0;
  const src = String(text || "").replace(/\r\n?/g, "\n");
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') { cur += '"'; i += 1; }
        else { inQuotes = false; }
      } else {
        cur += ch;
      }
      continue;
    }
    if (ch === '"') { inQuotes = true; continue; }
    if (ch === ",") { cells[row].push(cur); cur = ""; continue; }
    if (ch === "\n") {
      cells[row].push(cur); cur = "";
      cells.push([]); row += 1;
      continue;
    }
    cur += ch;
  }
  if (cur.length || cells[row].length > 0) cells[row].push(cur);
  while (cells.length && cells[cells.length - 1].length === 1 && cells[cells.length - 1][0] === "") {
    cells.pop();
  }
  if (cells.length === 0) return { headers: [], rows: [] };
  const headers = cells[0].map((h) => h.trim());
  const rows = cells.slice(1).map((c) => {
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (c[i] ?? "").trim(); });
    return obj;
  });
  return { headers, rows };
}

function coerceValue(v) {
  if (v == null || v === "") return undefined;
  const n = Number(String(v).replace(/[$,₹€£]/g, "").trim());
  return Number.isFinite(n) ? n : undefined;
}

function buildLeadRows(parsedRows, mapping) {
  // mapping: { [csvHeader]: leadFieldKey }
  return parsedRows.map((row) => {
    const lead = {};
    for (const [header, fieldKey] of Object.entries(mapping)) {
      if (!fieldKey) continue;
      const raw = row[header];
      if (raw === undefined || raw === "") continue;
      if (fieldKey === "labels") {
        lead.labels = String(raw).split(",").map((s) => s.trim()).filter(Boolean);
      } else if (fieldKey === "value") {
        const num = coerceValue(raw);
        if (num !== undefined) lead.value = num;
      } else if (fieldKey === "status") {
        const s = normalizeLeadStatus(raw);
        if (s) lead.status = s;
      } else {
        lead[fieldKey] = String(raw).trim();
      }
    }
    return lead;
  });
}

function Step({ active, done, n, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, color: active ? "var(--color-text-primary)" : "var(--color-text-secondary)" }}>
      <div style={{
        width: 22, height: 22, borderRadius: 999, fontSize: 11, fontWeight: 600,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: done ? "var(--crm-success, rgb(16,185,129))" : active ? "var(--crm-primary)" : "var(--color-bg-muted)",
        color: done || active ? "#fff" : "var(--color-text-secondary)",
      }}>{done ? "✓" : n}</div>
      <div style={{ fontSize: 13, fontWeight: active ? 600 : 500 }}>{label}</div>
    </div>
  );
}

export default function BulkImportLeadsModal({ open, onClose }) {
  const [step, setStep] = useState(1);
  const [csvText, setCsvText] = useState("");
  const [mapping, setMapping] = useState({}); // header -> field key
  const [submitResult, setSubmitResult] = useState(null); // { summary, errors }
  const [serverDupes, setServerDupes] = useState([]); // parallel array, one entry per previewRow: { matches: [...] }
  const bulkCreate = useBulkCreateLeads();
  const bulkDupCheck = useBulkDuplicateCheck();
  // Imported leads must inherit the currently-selected project — otherwise
  // they land with projectId=null and the Leads list (which is always
  // project-scoped) won't surface them. CSV file itself cannot specify a
  // project, so we stamp every row server-side-style on the client.
  const { projectId: selectedProjectId } = useSelectedProject();

  const parsed = useMemo(() => (step >= 2 ? parseCsv(csvText) : { headers: [], rows: [] }), [csvText, step]);

  const reset = () => {
    setStep(1); setCsvText(""); setMapping({}); setSubmitResult(null); setServerDupes([]);
  };
  const close = () => { reset(); onClose?.(); };

  const handleFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    setCsvText(text);
  };

  const goToMap = () => {
    const p = parseCsv(csvText);
    if (p.headers.length === 0 || p.rows.length === 0) {
      toast.error("No data found in CSV.");
      return;
    }
    const guessed = {};
    p.headers.forEach((h) => { guessed[h] = guessField(h); });
    setMapping(guessed);
    setStep(2);
  };

  const titleHeader = Object.entries(mapping).find(([, k]) => k === "title")?.[0];
  const mappedRows = useMemo(
    () => (step >= 3 ? buildLeadRows(parsed.rows, mapping) : []),
    [step, parsed.rows, mapping],
  );
  const previewRows = useMemo(
    () => buildLeadRows(parsed.rows, mapping),
    [parsed.rows, mapping],
  );
  // Fire the server-side bulk duplicate check whenever we land on step 3 with
  // rows to import. Cheap to re-run on mapping changes — single aggregated
  // query server-side. Reset when leaving the step.
  useEffect(() => {
    if (step !== 3 || submitResult || previewRows.length === 0) {
      if (step !== 3) setServerDupes([]);
      return;
    }
    let cancelled = false;
    const rows = previewRows.map((r) => ({
      email: r.email || "",
      phone: r.phone || "",
      brandName: r.brandName || "",
    }));
    bulkDupCheck.mutate(rows, {
      onSuccess: (results) => { if (!cancelled) setServerDupes(results || []); },
      onError: () => { if (!cancelled) setServerDupes([]); },
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, submitResult, JSON.stringify(previewRows.map((r) => [r.email, r.phone, r.brandName]))]);

  const previewIssues = useMemo(() => {
    const seen = new Set();
    const issues = [];
    previewRows.forEach((r, i) => {
      if (!r.title) { issues.push({ index: i, reason: "missing title" }); return; }
      const bn = (r.brandName || "").trim().toLowerCase();
      if (bn) {
        if (seen.has(bn)) issues.push({ index: i, reason: `duplicate brand "${r.brandName}" in batch` });
        else seen.add(bn);
      }
    });
    return issues;
  }, [previewRows]);

  const submit = () => {
    if (!selectedProjectId) {
      toast.error("Pick a project first — imported leads need a project.");
      return;
    }
    // Stamp the active project on every row so the imported leads show up
    // in the project-scoped Leads list. CSV rows themselves never carry a
    // project (per-row project isn't meaningful for a single-project import).
    const rowsWithProject = mappedRows.map((r) => ({ ...r, projectId: selectedProjectId }));
    bulkCreate.mutate(rowsWithProject, {
      onSuccess: (data) => {
        setSubmitResult(data);
        const { created = 0, failed = 0 } = data?.summary || {};
        if (failed === 0) toast.success(`Imported ${created} ${created === 1 ? "lead" : "leads"}`);
        else toast.success(`Imported ${created}, ${failed} failed`);
      },
      onError: (err) => {
        const msg = err?.response?.data?.error || err?.message || "Import failed";
        toast.error(msg);
      },
    });
  };

  return (
    <Modal open={open} onClose={close} width={760}>
      <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--color-border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <h2 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>Import leads</h2>
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 4 }}>
            Paste a CSV or upload a file. Up to 500 leads per import.
          </div>
        </div>
        <button onClick={close} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--color-text-secondary)", padding: 4 }} aria-label="Close">
          <X size={18} />
        </button>
      </div>

      <div style={{ padding: "12px 20px", borderBottom: "1px solid var(--color-border-subtle)", display: "flex", gap: 18, alignItems: "center" }}>
        <Step n={1} active={step === 1} done={step > 1} label="Source" />
        <ArrowRight size={14} style={{ color: "var(--color-text-secondary)" }} />
        <Step n={2} active={step === 2} done={step > 2} label="Map columns" />
        <ArrowRight size={14} style={{ color: "var(--color-text-secondary)" }} />
        <Step n={3} active={step === 3} done={!!submitResult} label="Review & import" />
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
        {step === 1 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {/* Template prompt — first thing the user sees on step 1. Bigger
                than a footnote because users almost always want this on a
                first-time import. */}
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 12px", borderRadius: 8,
              background: "var(--crm-primary-soft)",
              border: "1px solid var(--color-border-subtle)",
            }}>
              <FileText size={15} style={{ color: "var(--crm-primary)", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: "var(--color-text-primary)" }}>
                Not sure about the format? Download our CSV template — it includes every supported column with two example rows.
              </div>
              <button
                type="button"
                onClick={downloadTemplate}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "6px 12px", borderRadius: 8,
                  background: "var(--crm-primary)", color: "#fff",
                  border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                <Download size={13} /> Download template
              </button>
            </div>

            <label style={{ display: "block", fontSize: 13, fontWeight: 600 }}>Paste CSV</label>
            <textarea
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              placeholder={`title (or POC Name),brandName,email,phone,value\nAcme renewal,Acme Inc,jane@acme.com,+15551234567,12000\n…`}
              rows={10}
              style={{ width: "100%", padding: 12, borderRadius: 8, border: "1px solid var(--color-border-subtle)", background: "var(--color-bg-surface)", color: "var(--color-text-primary)", fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12, lineHeight: 1.5, resize: "vertical", outline: "none" }}
            />
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
              First row should be column headers. Title or "POC Name" both work. Common names like "company", "email", "phone" auto-map. Commas inside quoted fields are fine.
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 8, border: "1px dashed var(--color-border-subtle)", cursor: "pointer", fontSize: 13, color: "var(--color-text-secondary)" }}>
                <Upload size={14} /> Upload .csv file
                <input type="file" accept=".csv,text/csv" onChange={(e) => handleFile(e.target.files?.[0])} style={{ display: "none" }} />
              </label>
              {csvText && (
                <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
                  {csvText.split("\n").filter(Boolean).length - 1} data row{csvText.split("\n").filter(Boolean).length - 1 === 1 ? "" : "s"} detected
                </span>
              )}
            </div>
          </div>
        )}

        {step === 2 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>
              Match each CSV column to a lead field. Columns you skip won't be imported. <strong style={{ color: "var(--color-text-primary)" }}>Title or POC Name is required.</strong>
            </div>
            <div style={{ border: "1px solid var(--color-border-subtle)", borderRadius: 8, overflow: "hidden" }}>
              {parsed.headers.map((header) => {
                const sample = parsed.rows[0]?.[header] || "";
                return (
                  <div key={header} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, alignItems: "center", padding: "10px 14px", borderBottom: "1px solid var(--color-border-subtle)" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{header || <em>(empty header)</em>}</div>
                      <div style={{ fontSize: 11, color: "var(--color-text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={sample}>{sample || <em>—</em>}</div>
                    </div>
                    <ArrowRight size={14} style={{ color: "var(--color-text-secondary)" }} />
                    <AntSelect
                      value={mapping[header] || undefined}
                      onChange={(v) => setMapping({ ...mapping, [header]: v || "" })}
                      placeholder="— skip —"
                      allowClear
                      options={FIELDS.map((f) => ({ value: f.key, label: `${f.label}${f.required ? " *" : ""}` }))}
                      style={{ width: "100%", height: 32 }}
                    />
                  </div>
                );
              })}
            </div>
            {!titleHeader && (
              <div style={{ fontSize: 12, color: "var(--crm-danger)", display: "flex", alignItems: "center", gap: 6 }}>
                <AlertCircle size={13} /> Map at least one column to "Title" or "POC Name" before continuing.
              </div>
            )}
          </div>
        )}

        {step === 3 && !submitResult && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>
              {previewRows.length} {previewRows.length === 1 ? "row" : "rows"} ready to import.
              {previewIssues.length > 0 && (
                <span style={{ color: "var(--crm-warning, rgb(245,158,11))", marginLeft: 8 }}>
                  {previewIssues.length} will be skipped (see below).
                </span>
              )}
              {(() => {
                const n = serverDupes.filter((d) => d?.matches?.length > 0).length;
                if (!n) return null;
                return (
                  <span style={{ color: "var(--crm-warning, rgb(245,158,11))", marginLeft: 8 }}>
                    {n} look like duplicates of existing leads.
                  </span>
                );
              })()}
              {bulkDupCheck.isPending && (
                <span style={{ color: "var(--color-text-secondary)", marginLeft: 8 }}>Checking for duplicates…</span>
              )}
            </div>
            <div style={{ border: "1px solid var(--color-border-subtle)", borderRadius: 8, overflow: "hidden" }}>
              <div style={{ display: "grid", gridTemplateColumns: "30px 1fr 1fr 100px 90px", gap: 8, padding: "8px 12px", background: "var(--color-bg-muted)", fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", }}>
                <div>#</div><div>Title</div><div>Brand · Email</div><div>Value</div><div>Status</div>
              </div>
              <div style={{ maxHeight: 280, overflowY: "auto" }}>
                {previewRows.slice(0, 200).map((r, i) => {
                  const issue = previewIssues.find((x) => x.index === i);
                  const dupMatches = serverDupes[i]?.matches || [];
                  const hasDup = !issue && dupMatches.length > 0;
                  return (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "30px 1fr 1fr 100px 90px", gap: 8, padding: "8px 12px", borderTop: "1px solid var(--color-border-subtle)", fontSize: 12, background: issue ? "var(--crm-danger-soft)" : hasDup ? "var(--crm-warning-soft, rgba(245,158,11,0.10))" : "transparent", color: issue ? "var(--crm-danger)" : "var(--color-text-primary)" }}>
                      <div style={{ color: "var(--color-text-secondary)" }}>{i + 1}</div>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }} title={r.title || ""}>
                        {hasDup && (
                          <AlertTriangle size={12} style={{ color: "var(--crm-warning, rgb(245,158,11))", flexShrink: 0 }} aria-label="possible duplicate" />
                        )}
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {r.title || <em>missing</em>}
                        </span>
                      </div>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={`${r.brandName || ""} · ${r.email || ""}`}>
                        {r.brandName || "—"}{r.email ? ` · ${r.email}` : ""}
                        {hasDup && (
                          <span style={{ marginLeft: 6, color: "var(--crm-warning, rgb(245,158,11))" }} title={dupMatches.map((m) => m.title).join(", ")}>
                            (matches “{dupMatches[0].title}”{dupMatches.length > 1 ? ` +${dupMatches.length - 1}` : ""})
                          </span>
                        )}
                      </div>
                      <div style={{ fontVariantNumeric: "tabular-nums" }}>{r.value ?? "—"}</div>
                      <div>{r.status || "open"}</div>
                    </div>
                  );
                })}
                {previewRows.length > 200 && (
                  <div style={{ padding: 10, fontSize: 12, color: "var(--color-text-secondary)", textAlign: "center", borderTop: "1px solid var(--color-border-subtle)" }}>
                    +{previewRows.length - 200} more rows not shown
                  </div>
                )}
              </div>
            </div>
            {previewIssues.length > 0 && (
              <div style={{ background: "var(--crm-warning-soft, rgba(245,158,11,0.12))", color: "var(--crm-warning, rgb(245,158,11))", padding: 10, borderRadius: 8, fontSize: 12 }}>
                {previewIssues.slice(0, 5).map((x) => (
                  <div key={x.index}>Row {x.index + 1}: {x.reason}</div>
                ))}
                {previewIssues.length > 5 && <div>… and {previewIssues.length - 5} more</div>}
              </div>
            )}
          </div>
        )}

        {step === 3 && submitResult && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ background: "var(--crm-success-soft, rgba(16,185,129,0.12))", color: "var(--crm-success, rgb(16,185,129))", padding: 14, borderRadius: 8, display: "flex", alignItems: "flex-start", gap: 10 }}>
              <CheckCircle2 size={18} style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>Import complete</div>
                <div style={{ fontSize: 13 }}>
                  {submitResult.summary?.created || 0} created · {submitResult.summary?.failed || 0} failed
                </div>
              </div>
            </div>
            {(submitResult.errors || []).length > 0 && (
              <div style={{ border: "1px solid var(--color-border-subtle)", borderRadius: 8, overflow: "hidden" }}>
                <div style={{ padding: "10px 14px", background: "var(--color-bg-muted)", fontSize: 12, fontWeight: 600 }}>
                  Errors ({submitResult.errors.length})
                </div>
                <div style={{ maxHeight: 220, overflowY: "auto" }}>
                  {submitResult.errors.map((e, i) => (
                    <div key={i} style={{ padding: "8px 14px", borderTop: "1px solid var(--color-border-subtle)", fontSize: 12 }}>
                      Row {e.index + 1}: <span style={{ color: "var(--crm-danger)" }}>{e.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ padding: "12px 20px", borderTop: "1px solid var(--color-border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
          {step === 1 && <span><FileText size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} /> CSV with headers</span>}
          {step === 2 && <span>{parsed.rows.length} rows parsed</span>}
          {step === 3 && !submitResult && <Badge tone={previewIssues.length ? "warning" : "success"}>{previewRows.length - previewIssues.length} importable</Badge>}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {step > 1 && !submitResult && (
            <Button variant="secondary" leftIcon={<ArrowLeft size={14} />} onClick={() => setStep(step - 1)}>
              Back
            </Button>
          )}
          {step === 1 && (
            <Button variant="primary" onClick={goToMap} disabled={!csvText.trim()}>
              Next: map columns
            </Button>
          )}
          {step === 2 && (
            <Button variant="primary" onClick={() => setStep(3)} disabled={!titleHeader}>
              Next: review
            </Button>
          )}
          {step === 3 && !submitResult && (
            <Button variant="primary" onClick={submit} disabled={bulkCreate.isPending || previewRows.length === 0}>
              {bulkCreate.isPending ? "Importing…" : `Import ${previewRows.length - previewIssues.length} leads`}
            </Button>
          )}
          {step === 3 && submitResult && (
            <>
              <Button variant="secondary" onClick={reset}>Import more</Button>
              <Button variant="primary" onClick={close}>Done</Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
